// filepath: apps/web/src/design-system/Banner.tsx
//
// FR-UI-CORE-9 / FR-UI-OPS-3 — Banner primitive from the design system
// inventory (`docs/guides/design system/components/feedback/Banner.jsx`).
//
// Ported from the reference JSX to TypeScript for the production
// React SPA. Renders with `role="alert"` so the in-page announcer
// surfaces the message to assistive tech without stealing focus.

import React from "react";

export type BannerTone = "warning" | "danger" | "info";

export interface BannerProps {
  /** Visual tone. Default: `warning`. */
  tone?: BannerTone;
  children?: React.ReactNode;
  /** When set, surfaces a Dismiss action that calls this handler. */
  onDismiss?: () => void;
  /** Test hook forwarded onto the rendered element. */
  "data-testid"?: string;
  /** Extra className passthrough. */
  className?: string;
  /** Inline style passthrough (token-only). */
  style?: React.CSSProperties;
}

interface TonePalette {
  bg: string;
  text: string;
  border: string;
}

const TONE_PALETTES: Record<BannerTone, TonePalette> = {
  warning: {
    bg: "var(--color-warning-subtle)",
    text: "var(--color-warning-text)",
    border: "var(--amber-500)",
  },
  danger: {
    bg: "var(--color-danger-subtle)",
    text: "var(--color-danger-subtle-text)",
    border: "var(--red-500)",
  },
  info: {
    bg: "var(--color-primary-subtle)",
    text: "var(--color-primary-subtle-text)",
    border: "var(--blue-500)",
  },
};

/**
 * Banner — single-line inline alert with a Dismiss action (optional).
 * Pure presentation; no internal state.
 */
export function Banner({
  tone = "warning",
  children,
  onDismiss,
  style,
  className,
  ...rest
}: BannerProps): React.ReactElement {
  const palette = TONE_PALETTES[tone] ?? TONE_PALETTES.warning;
  return (
    <div
      {...rest}
      role="alert"
      className={["ds-banner", `ds-banner--${tone}`, className]
        .filter(Boolean)
        .join(" ")}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "10px 16px",
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
        borderRadius: "var(--radius-sm)",
        fontFamily: "var(--font-sans)",
        fontSize: "var(--text-sm)",
        ...style,
      }}
    >
      <span style={{ flex: "1 1 auto" }}>{children}</span>
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          className="ds-banner__dismiss"
          style={{
            border: "none",
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-medium)",
            textDecoration: "underline",
            fontFamily: "inherit",
          }}
        >
          Dismiss
        </button>
      ) : null}
    </div>
  );
}
